import { useMemo } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import {
  buildFloorPlanSrc,
  buildGalleryPhotos,
} from 'components/marketplace/helpers/galleryPhotos';
import type { GalleryPhoto } from 'components/marketplace/helpers/galleryPhotos';
import { useTrackProperty } from 'components/marketplace/helpers/useTrackProperty';
import { Map } from 'components/marketplace/property/Map';
import type { PropertyDetailsType } from 'components/marketplace/property/types';
import { generatePresetPictureSet } from 'components/shared/performance/pictures';
import type { PresetPictureSet } from 'components/shared/performance/pictures';

import styles from './GalleryPhotos.module.css';

const GALLERY_SOURCE_SET_CONFIG: PresetPictureSet = [
  {
    minWidth: 768,
    maxWidth: 1201,
    preset: '3:2-md',
    presetScale: '2/3',
  },
  {
    minWidth: 1201,
    maxWidth: 1400,
    preset: '3:2-lg',
    presetScale: '3/4',
  },
  {
    minWidth: 1400,
    maxWidth: null,
    preset: '3:2-xl',
    presetScale: '1',
  },
];

interface GalleryPhotoProps {
  photo: GalleryPhoto;
  idx: number;
  totalPhotos: number;
  address: string;
  toggleLightbox: () => void;
  setSelectedPhotoIndex: (idx: number) => void;
  isLastPhoto: boolean;
}

const GalleryPhotoTile = ({
  photo,
  idx,
  totalPhotos,
  address,
  toggleLightbox,
  setSelectedPhotoIndex,
  isLastPhoto,
}: GalleryPhotoProps) => {
  const { trackProperty } = useTrackProperty();
  const handleClick = () => {
    trackProperty('property-images', 'marketplace-pdp-lightbox');
    toggleLightbox();
    setSelectedPhotoIndex(idx);
  };

  const floorPlanLabel = photo.isFloorPlan ? photo.label?.trim() || 'Floor plan' : null;
  const alt = floorPlanLabel
    ? `${floorPlanLabel}${address ? ` of ${address}` : ''}`
    : `Photo ${idx + 1} of ${totalPhotos}${address ? ` of ${address}` : ''}`;

  return (
    <Box
      analyticsName={
        photo.isFloorPlan
          ? 'cosmos-marketplace-property-desktop-gallery-floor-plan'
          : 'cosmos-marketplace-property-desktop-gallery-photo'
      }
      aria-label={alt}
      key={`${idx}-photo`}
      onClick={handleClick}
      className={cn('relative w-full flex-col p-0', !isLastPhoto && 'pb-3')}
    >
      {photo.isFloorPlan ? (
        // Floor plans are often portrait or non-3:2, so the listing photo
        // preset (3:2-md/lg/xl) crops their edges off. Render them
        // uncropped, object-fit: contain, inside a 3:2 tile on a neutral
        // background so the full plan shows with letterboxing.
        <Box className="aspect-[3/2] w-full overflow-hidden bg-od-bg-secondary">
          <Image
            alt={alt}
            src={buildFloorPlanSrc(photo.url)}
            className="h-full w-full object-contain"
          />
        </Box>
      ) : (
        generatePresetPictureSet({
          debugImageSizes: false,
          photoUrl: photo.url,
          sizes: GALLERY_SOURCE_SET_CONFIG,
          lazy: idx !== 0,
          alt,
          preload: idx === 0,
          fetchPriority: idx === 0 ? 'high' : undefined,
        })
      )}
      {floorPlanLabel && (
        <Box className="absolute bottom-3 left-3 rounded-lg bg-od-bg-inverse-primary px-3 py-1">
          <Text as="span" className="text-label-sm text-od-content-inverse-primary">
            {floorPlanLabel}
          </Text>
        </Box>
      )}
    </Box>
  );
};

interface GalleryPhotosProps {
  property: PropertyDetailsType;
  toggleLightbox: () => void;
  handleSetSelectedPhotoIndex: (idx: number) => void;
}
export const GalleryPhotos = ({
  property,
  toggleLightbox,
  handleSetSelectedPhotoIndex,
}: GalleryPhotosProps) => {
  const galleryPhotos = useMemo(() => buildGalleryPhotos(property), [property]);
  const totalPhotos = galleryPhotos.length;
  const address = [property?.street, property?.city, property?.state].filter(Boolean).join(', ');

  const Gallery = useMemo(() => {
    const photos = galleryPhotos.map((photo, idx) => {
      const galleryPhoto = (
        <GalleryPhotoTile
          key={idx}
          idx={idx}
          totalPhotos={totalPhotos}
          address={address}
          photo={photo}
          toggleLightbox={toggleLightbox}
          setSelectedPhotoIndex={handleSetSelectedPhotoIndex}
          isLastPhoto={idx === totalPhotos - 1}
        />
      );

      if (idx == 1) {
        return (
          <Row key="split-photo-map-view" className="w-full justify-between pb-3">
            <Box className="aspect-[16/9] h-full w-[49.75%] overflow-hidden">{galleryPhoto}</Box>
            <Box className="aspect-[16/9] w-[49.75%] bg-od-bg-secondary">
              <Map
                key="property-location"
                latitude={property.latitude}
                longitude={property.longitude}
              />
            </Box>
          </Row>
        );
      }

      return galleryPhoto;
    });

    return photos;
  }, [
    galleryPhotos,
    totalPhotos,
    address,
    property.latitude,
    property.longitude,
    toggleLightbox,
    handleSetSelectedPhotoIndex,
  ]);
  return (
    <Col
      className={cn(
        styles.hideScrollbar,
        'h-full w-full items-center max-md:hidden md:basis-1/2 md:overflow-scroll xl:flex-1',
      )}
    >
      {Gallery}
    </Col>
  );
};
